import { tr } from '@/lib/i18n-core';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Package, Plus, Receipt, Trash2, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { humanize, money, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import type { SupervisorCredentials } from '@his/shared';
import { DiscountHint, SupervisorDialog, needsDiscountApproval, supervisorHeaders } from '../accounts/SupervisorApproval';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { AddPackagePanel, ChosenPackageGroup, type ChosenPackage } from './AddPackagePanel';
import { ServiceSearch, type ServiceOption } from './ServiceSearch';

interface LiveVisit {
  id: string;
  status: string;
  tokenNo: string | null;
  doctor: { id: string; name: string };
}

interface DraftLine {
  key: string;
  service: ServiceOption | null;
  qty: number;
  discountPct: number;
}

function lineListNet(line: DraftLine): number {
  const gross = Math.round((line.service?.basePriceMinor ?? 0) * line.qty);
  return gross - Math.round((gross * line.discountPct) / 100);
}

export function NewInvoicePage() {
  const { hasModule } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const workingBranchId = useWorkingBranchId();
  const toast = useToast();
  const returnTo = safeReturnTo(params.get('returnTo'), '/billing');
  const contexts = (['opd', 'ipd', 'pharmacy', 'lab', 'radiology', 'er'] as const).filter((c) => hasModule(c === 'pharmacy' ? 'pharmacy' : c));
  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [forVisit, setForVisit] = useState(true);
  const [context, setContext] = useState(contexts[0] ?? 'pharmacy');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [packages, setPackages] = useState<ChosenPackage[]>([]);
  const [addingPackage, setAddingPackage] = useState(false);
  const [discountReason, setDiscountReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [approval, setApproval] = useState<{ issue: boolean; message: string } | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const chosenLines = lines.filter((line): line is DraftLine & { service: ServiceOption } => line.service !== null);

  const liveVisit = useQuery({
    queryKey: ['invoice-live-visit', patientId, workingBranchId],
    queryFn: async () => (await get<{ visit: LiveVisit | null }>(`/invoices/live-visit${qs({ patientId, branchId: workingBranchId || undefined })}`)).visit,
    enabled: Boolean(patientId) && context === 'opd',
  });
  const visit = patientId && context === 'opd' ? (liveVisit.data ?? null) : null;

  const attempt = useRef<{ payload: string; key: string } | null>(null);

  const create = useMutation({
    mutationFn: ({ issue, supervisor, requestApproval }: { issue: boolean; supervisor?: SupervisorCredentials; requestApproval?: boolean }) => {
      const body = {
        patientId: patientId || undefined,
        branchId: workingBranchId || undefined,
        context,
        ...(visit ? { appointmentId: forVisit ? visit.id : null } : {}),
        lines: chosenLines.map((line) => ({
          chargeItemId: line.service.id,
          description: line.service.name,
          qty: line.qty,
          discountPct: line.discountPct,
        })),
        ...(packages.length > 0 ? { packages: packages.map((chosen) => ({ packageId: chosen.quote.packageId })) } : {}),
        discountReason: discountReason || undefined,
        issue,
        ...(requestApproval ? { requestDiscountApproval: true } : {}),
      };
      const payload = JSON.stringify(body);
      if (attempt.current?.payload !== payload) attempt.current = { payload, key: newIdempotencyKey() };
      return post<{ id: string }>('/invoices', body, { idempotencyKey: attempt.current.key, headers: supervisorHeaders(supervisor ?? null) });
    },
    onSuccess: (invoice, variables) => {
      attempt.current = null;
      setApproval(null);
      toast.success(
        variables.requestApproval ? tr('billing', 'estimate_saved_and_sent_for_discount_approval') : variables.issue ? tr('billing', 'invoice_created') : tr('billing', 'estimate_saved'),
      );
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/billing/invoices/${invoice.id}`, { replace: true });
    },
    onError: (caught, variables) => {
      if (needsDiscountApproval(caught) && !variables.supervisor) {
        setApproval({ issue: variables.issue, message: describeError(caught) });
        return;
      }
      if (variables.supervisor) {
        setApprovalError(describeError(caught));
        return;
      }
      setError(describeError(caught));
    },
  });

  const estimated = chosenLines.reduce(
    (sum, line) => sum + lineListNet(line),
    packages.reduce((sum, chosen) => sum + chosen.quote.totals.netMinor, 0),
  );
  const empty = chosenLines.length === 0 && packages.length === 0;
  const dirty = !create.isSuccess && (Boolean(patientId) || !empty || discountReason !== '');

  return (
    <FormScreen
      wide
      title={tr('billing', 'new_invoice')}
      description={tr('billing', 'prices_resolve_from_the_patients_category_and')}
      eyebrow={tr('billing', 'billing')}
      icon={Receipt}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (empty) return;
        setError(null);
        create.mutate({ issue: true });
      }}
    >
      <FormSection title={tr('billing', 'patient')} icon={UserRound}>
        <Field label={tr('billing', 'patient')} hint={tr('billing', 'leave_blank_for_a_walk_in_sale')}>
          <PatientPicker
            value={patientId}
            onChange={(id) => {
              setPatientId(id);
              setForVisit(true);
            }}
          />
        </Field>
        <Field label={tr('billing', 'context')}>
          <Select value={context} onChange={(e) => setContext(e.target.value as typeof context)}>
            {contexts.map((c) => (
              <option key={c} value={c}>
                {humanize(c, 'invoice_context')}
              </option>
            ))}
          </Select>
        </Field>
        {visit && (
          <Field label={tr('billing', 'visit')} hint={tr('billing', 'a_bill_on_todays_visit_is_paid_before_the_consultation')} className="sm:col-span-2">
            <ChoiceButtons
              layout="cards"
              value={forVisit ? 'visit' : 'none'}
              onChange={(value) => setForVisit(value === 'visit')}
              options={[
                {
                  value: 'visit',
                  label: `${tr('billing', 'todays_visit_with_x', { doctor: visit.doctor.name })}${visit.tokenNo ? ` · ${visit.tokenNo}` : ''}`,
                },
                { value: 'none', label: tr('billing', 'not_for_a_visit') },
              ]}
            />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('billing', 'services')} icon={ListChecks} columns={1}>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setAddingPackage(true)} disabled={addingPackage}>
            <Package className="h-4 w-4" /> {tr('billing', 'add_package')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setLines((current) => [...current, { key: newIdempotencyKey(), service: null, qty: 1, discountPct: 0 }])}
          >
            <Plus className="h-4 w-4" /> {tr('billing', 'add_line')}
          </Button>
        </div>

        {addingPackage && (
          <AddPackagePanel
            patientId={patientId}
            branchId={workingBranchId || null}
            onCancel={() => setAddingPackage(false)}
            onAdd={(quote) => {
              setPackages((current) => [...current, { key: `${quote.packageId}:${Date.now()}:${current.length}`, quote }]);
              setAddingPackage(false);
            }}
          />
        )}

        {packages.map((chosen) => (
          <ChosenPackageGroup key={chosen.key} chosen={chosen} onRemove={() => setPackages((current) => current.filter((c) => c.key !== chosen.key))} />
        ))}

        {empty && lines.length === 0 && !addingPackage && (
          <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">{tr('billing', 'add_at_least_one_service_or_package')}</p>
        )}

        {lines.map((line, index) => {
          return (
            <div key={line.key} className="grid gap-2 rounded-md border p-2 sm:grid-cols-12">
              <div className="sm:col-span-6">
                {line.service ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate">
                      {line.service.name} <span className="text-xs text-muted-foreground">({line.service.code})</span>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setLines((current) => current.map((l, i) => (i === index ? { ...l, service: null } : l)))}
                    >
                      {tr('billing', 'change')}
                    </Button>
                  </div>
                ) : (
                  <ServiceSearch
                    autoFocus
                    onChoose={(service) => setLines((current) => current.map((l, i) => (i === index ? { ...l, service } : l)))}
                  />
                )}
              </div>
              <Input
                className="sm:col-span-2"
                type="number"
                min={1}
                value={line.qty}
                onChange={(e) => setLines((current) => current.map((l, i) => (i === index ? { ...l, qty: Number(e.target.value) } : l)))}
              />
              <Input
                className="sm:col-span-2"
                type="number"
                min={0}
                max={100}
                value={line.discountPct}
                onChange={(e) => setLines((current) => current.map((l, i) => (i === index ? { ...l, discountPct: Number(e.target.value) } : l)))}
                placeholder={tr('billing', 'disc')}
              />
              <div className="flex items-center justify-end gap-2 sm:col-span-2">
                {line.service && (
                  <span className="text-end">
                    <span className="block text-[11px] text-muted-foreground">{tr('billing', 'list_price')}</span>
                    <span className="tabular text-sm">{money(lineListNet(line))}</span>
                  </span>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={tr('billing', 'remove_line')}
                  onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </FormSection>

      <FormSection title={tr('billing', 'summary')} icon={Receipt} columns={1}>
        {chosenLines.some((l) => l.discountPct > 0) && (
          <Field label={tr('billing', 'reason_for_discount')} required hint={tr('billing', 'required_whenever_a_discount_is_applied')}>
            <Input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} />
          </Field>
        )}
        <p className="text-xs text-muted-foreground">
          <DiscountHint />
        </p>
        <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
          <span className="text-sm text-muted-foreground">{tr('billing', 'estimated_total_list_prices')}</span>
          <span className="tabular text-lg font-semibold">{money(estimated)}</span>
        </div>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={tr('billing', 'create_and_issue')}
        loading={create.isPending}
        incomplete={empty}
        cancelTo={returnTo}
        secondary={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null);
              create.mutate({ issue: false });
            }}
            disabled={empty || create.isPending}
          >
            {tr('billing', 'save_as_estimate')}
          </Button>
        }
      />

      {approval && (
        <SupervisorDialog
          open
          title={tr('billing', 'discount_above_your_limit')}
          message={approval.message}
          busy={create.isPending}
          error={approvalError}
          onClose={() => {
            setApproval(null);
            setApprovalError(null);
          }}
          onApprove={(credentials) => {
            setApprovalError(null);
            create.mutate({ issue: approval.issue, supervisor: credentials });
          }}
          alternative={{
            label: tr('billing', 'send_for_approval_instead'),
            onClick: () => {
              setApproval(null);
              create.mutate({ issue: false, requestApproval: true });
            },
          }}
        />
      )}
    </FormScreen>
  );
}
